import { useMemo } from "react";
import { startOfDay } from "date-fns";
import { useTranslation } from "react-i18next";
import { useHotel } from "@/hooks/useHotel";
import {
  buildNavigationGroups,
  buildHotelSettingsNavigationGroups,
  buildNavigationGroupsAdmin,
  buildNavigationGroupsSuperAdmin,
  type NavGroup,
} from "@/components/layout/constants/navigation";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useLayoutRole } from "@/hooks/useLayoutRole";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { useUnreadCount } from "@/components/layout/hooks/useUnreadCount";
import {
  useCurrentSystemNotificationSetting,
  useUnreadNotificationsCount,
} from "@/hooks/useNotifications";
import { useRequests } from "@/components/pages/requests/hooks/useRequests";
import { useChatClient } from "@/components/pages/chats/hooks/useChatClient";
import { useTodaySettlement } from "@/components/pages/settlement/hooks/useTodaySettlement";
import { useInternalChatClient } from "@/components/pages/chats/hooks/useInternalChatClient";
import {
  COMMUNICATION_AUDIT_ROLES,
  COMMUNICATION_INTEGRATION_ROLES,
  type CapabilityMap,
  canAccessAdminSettingsPage,
  hasAnyRole,
  hasCorePlatformAccess,
  hasCorePlatformEnabled,
  hasPlatformPermission,
  hasKnownPlatformPermissions,
  hasTenantPermission,
  hasFrontdeskBlockPermission,
  isCustomFrontDeskRole,
  isRapidPlan,
  isPlatformAdminRole,
  isSuperAdminRole,
  isTenantAdminRole,
  PlatformRole,
  PMS_INTEGRATION_ROLES,
  type CorePlatformFeatureKey,
  type PlatformPermissionKey,
} from "@/utils/accessControl";
import type { UserProfile } from "@/services/authService";
import { getFrontdeskBlockForPath } from "@/constants/frontdeskPermissions";
import { isRoomsNavHiddenForPlan } from "@/components/layout/utils/roomsNavVisibility";

interface NavigationMenus {
  defaultNavigation: NavGroup[];
  settingsNavigation: NavGroup[];
  allNavigation: NavGroup[];
}

type HotelSidebarRole = "HOTEL_ADMIN" | "FRONT_DESK" | "STAFF" | undefined;

export interface NavigationVisibilityContext {
  capabilities: CapabilityMap;
  isHotelScopedRole: boolean;
  role?: string;
  userProfile: UserProfile | null;
}

const PLATFORM_PERMISSION_BY_NAV_PATH: Partial<
  Record<string, PlatformPermissionKey>
> = {
  "/tenants": "platform:tenants:read",
  "/users": "platform:users:read",
  "/subscriptions": "platform:subscriptions:read",
  "/partners": "platform:partners:read",
  "/partners/destinations": "platform:partners:read",
  "/partners/revenue": "platform:partners:read",
  "/analytics": "platform:analytics:read",
  "/admin/analytics": "platform:analytics:read",
  "/monitoring": "platform:monitoring:read",
  "/monitoring-settings": "platform:monitoring:read",
  "/users-monitoring": "platform:monitoring:read",
  "/settings": "platform:settings:read",
  "/regions": "platform:settings:read",
  "/storage": "platform:settings:read",
  "/modules": "platform:settings:read",
  "/edit-privacy-policy": "platform:settings:read",
  "/edit-terms-of-use": "platform:settings:read",
  "/app-links-settings": "platform:settings:read",
  "/data-deletion": "platform:tenants:read",
  "/data-export": "platform:tenants:read",
};

const CORE_NAVIGATION_FEATURE_BY_PATH: Partial<
  Record<string, CorePlatformFeatureKey>
> = {
  "/arrivals": "arrivals",
  "/in-house": "in_house",
  "/departures": "departures",
  "/general-info": "hotel_brand_settings",
  "/reservations": "reservations",
  "/reservations/rooms": "rooms",
  "/reservations/guests": "guests",
  "/amenities": "amenities",
  "/pms-integration": "pms",
  "/sso-settings": "sso",
  "/notifications-settings": "notifications",
  "/mobile-settings": "hotel_brand_settings",
  "/mobile-settings/dashboard": "hotel_brand_settings",
};

export const isNavigationHrefVisible = (
  href: string,
  context: NavigationVisibilityContext,
): boolean => {
  const { capabilities, isHotelScopedRole, userProfile } = context;
  const requiredCoreFeature = CORE_NAVIGATION_FEATURE_BY_PATH[href];
  const frontdeskBlock = getFrontdeskBlockForPath(href);

  if (isRoomsNavHiddenForPlan(href, userProfile)) {
    return false;
  }

  if (
    isCustomFrontDeskRole(userProfile) &&
    frontdeskBlock !== null &&
    !hasFrontdeskBlockPermission(userProfile, frontdeskBlock)
  ) {
    return false;
  }

  if (
    requiredCoreFeature !== undefined &&
    !hasCorePlatformAccess(userProfile, requiredCoreFeature)
  ) {
    return false;
  }

  const requiredPlatformPermission = PLATFORM_PERMISSION_BY_NAV_PATH[href];
  if (
    requiredPlatformPermission !== undefined &&
    isPlatformAdminRole(userProfile) &&
    hasKnownPlatformPermissions(userProfile) &&
    !hasPlatformPermission(userProfile, requiredPlatformPermission)
  ) {
    return false;
  }

  const coreEnabled = !isHotelScopedRole || hasCorePlatformEnabled(userProfile);

  const isTenantAdminScope = isTenantAdminRole(userProfile);
  const tenantCanRead = (resource: string): boolean =>
    !isTenantAdminScope ||
    hasTenantPermission(userProfile, `${resource}:read`) ||
    hasTenantPermission(userProfile, `${resource}:administrate`);

  switch (href) {
    case "/data-deletion":
      // GDPR erasure is SUPER_ADMIN-only — hide from GUESTERIA_ADMIN.
      return isSuperAdminRole(userProfile);
    case "/data-export":
      // GDPR export audit list is SUPER_ADMIN-only — hide from GUESTERIA_ADMIN.
      return isSuperAdminRole(userProfile);
    case "/tenants":
      return isPlatformAdminRole(userProfile);
    case "/users":
      return isPlatformAdminRole(userProfile) || tenantCanRead("users");
    case "/hotels":
      return !isTenantAdminScope || tenantCanRead("hotel_brands");
    case "/reservations/rooms":
      return tenantCanRead("rooms");
    case "/billing-settings":
    case "/billing-subscriptions":
    case "/billing-transactions":
    case "/subscription-payments":
      return coreEnabled && capabilities.canViewBilling;
    case "/guesteria-partners-revenue":
      return (
        !isRapidPlan(userProfile) && coreEnabled && capabilities.canViewBilling
      );
    case "/settings":
      return canAccessAdminSettingsPage(userProfile);
    case "/sso-settings":
      return (
        isPlatformAdminRole(userProfile) ||
        (isTenantAdminScope && tenantCanRead("settings"))
      );
    case "/notifications-settings":
      return tenantCanRead("settings");
    case "/pms-integration":
      // The PMS endpoints are restricted to hotel, tenant and super admins, so
      // for anyone else the page can only ever render 403 errors. This has to
      // use the same predicate as the route guard: `role` carries the custom
      // role's display name when one is assigned, and only `hasAnyRole` looks
      // through to the base role underneath it.
      return hasAnyRole(userProfile, PMS_INTEGRATION_ROLES);
    case "/custom-pages":
      return capabilities.canUseCustomPages;
    case "/faqs":
      return capabilities.canManageFaqs;
    case "/services":
      return capabilities.canManageServices;
    case "/feedbacks":
    case "/feedback-settings":
      return capabilities.canUseFeedbacks;
    case "/partner-integration-settings":
      return capabilities.canUseGuesteriaPartners;
    case "/staff":
    case "/staff-schedule":
      return capabilities.canManageStaffOperations;
    case "/checkin-settings":
    case "/checkout-settings":
      return capabilities.canUseCheckinCheckout;
    case "/messaging-settings":
      return coreEnabled && capabilities.canUseGuestCommunication;
    case "/communication-channels":
    case "/communication-integrations":
      return (
        coreEnabled &&
        capabilities.canUseGuestCommunication &&
        hasAnyRole(userProfile, COMMUNICATION_INTEGRATION_ROLES)
      );
    case "/communication-deliveries":
      return (
        coreEnabled &&
        capabilities.canUseGuestCommunication &&
        hasAnyRole(userProfile, COMMUNICATION_AUDIT_ROLES)
      );
    case "/requests":
    case "/requests-settings":
      return capabilities.canUseRequestsSystem;
    case "/chats":
    case "/canned-responses":
      return capabilities.canUseGuestCommunication;
    case "/ai":
      return capabilities.canUseAiAssistant;
    case "/internal-chats":
      return capabilities.canUseInternalChat;
    case "/guesteria-partners":
      return capabilities.canUseGuesteriaPartners;
    default:
      return true;
  }
};

export const filterNavigationItemsByVisibility = (
  items: NavGroup["items"],
  context: NavigationVisibilityContext,
): NavGroup["items"] =>
  items
    .map((item) => {
      const nextChildren =
        item.items !== undefined
          ? filterNavigationItemsByVisibility(item.items, context)
          : undefined;
      const isItemVisible =
        item.alwaysVisible || isNavigationHrefVisible(item.href, context);
      const hasVisibleChildren =
        nextChildren !== undefined && nextChildren.length > 0;

      if (!isItemVisible && !hasVisibleChildren) {
        return null;
      }

      return {
        ...item,
        ...(nextChildren !== undefined ? { items: nextChildren } : {}),
        ...(!isItemVisible && hasVisibleChildren ? { isGroupOnly: true } : {}),
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

export const filterNavigationGroupsByVisibility = (
  groups: NavGroup[],
  context: NavigationVisibilityContext,
): NavGroup[] =>
  groups
    .map((group) => {
      const items = filterNavigationItemsByVisibility(group.items, context);

      return {
        ...group,
        items,
      };
    })
    .filter((group) => group.items.length > 0);

const getHotelSidebarRole = (role?: string): HotelSidebarRole => {
  if (role === "HOTEL_ADMIN" || role === "FRONT_DESK" || role === "STAFF") {
    return role as HotelSidebarRole;
  }

  return undefined;
};

export const useGetNavigationMenu = (): NavigationMenus => {
  const { t } = useTranslation();
  const { userProfile, isLoading: isAccessLoading } = useAuthenticatedUser();
  const layoutRole = useLayoutRole();
  const isHotelScopedRole = layoutRole === "HOTEL_SCOPED";
  const { capabilities } = useAccessCapabilities();
  const { arrivals, departures, inHouse } =
    useTodaySettlement(isHotelScopedRole);

  const { hotelId } = useHotel(isHotelScopedRole);
  const { data: requestsData } = useRequests(
    {
      dueDateTime: startOfDay(new Date()).toISOString(),
      limit: 1000,
      hotelId,
    },
    isHotelScopedRole && !!hotelId && capabilities.canUseRequestsSystem,
  );

  const requestsCount = useMemo(() => {
    return (
      requestsData?.data?.filter((request) =>
        request?.tasks?.every(
          (t) => t.status !== "CANCELED" && t.status !== "DONE",
        ),
      ).length || 0
    );
  }, [requestsData]);
  const { data: requestUnreadNotifications } = useUnreadNotificationsCount({
    enabled:
      isHotelScopedRole && !!hotelId && capabilities.canUseRequestsSystem,
    hotelId,
    type: "TASK",
  });
  const requestsUnreadCount = requestUnreadNotifications?.count ?? 0;
  const canInitializeChat = isHotelScopedRole && !isAccessLoading;

  const { client: internalChatsClient } = useInternalChatClient(
    canInitializeChat && capabilities.canUseInternalChat,
  );
  const { client: guestChatsClient } = useChatClient(
    hotelId ?? null,
    canInitializeChat && capabilities.canUseGuestCommunication,
  );
  const { data: newChatMessagePolicy } = useCurrentSystemNotificationSetting(
    "NEW_CHAT_MESSAGE",
    isHotelScopedRole &&
      (capabilities.canUseGuestCommunication ||
        capabilities.canUseInternalChat),
  );
  const isNewChatMessageNotificationEnabled =
    newChatMessagePolicy?.enabled ?? true;

  const {
    totalUnreadCount: externalTotal,
    unreadChannelsCount: externalUnread,
  } = useUnreadCount(
    canInitializeChat &&
      capabilities.canUseGuestCommunication &&
      isNewChatMessageNotificationEnabled
      ? guestChatsClient
      : null,
    { unresolvedOnly: true },
  );
  const {
    totalUnreadCount: internalTotal,
    unreadChannelsCount: internalUnread,
  } = useUnreadCount(
    canInitializeChat &&
      capabilities.canUseInternalChat &&
      isNewChatMessageNotificationEnabled
      ? internalChatsClient
      : null,
  );

  const role = userProfile?.role;
  const navigationVisibilityContext: NavigationVisibilityContext = {
    capabilities,
    isHotelScopedRole,
    role,
    userProfile,
  };

  if (layoutRole === PlatformRole.SUPER_ADMIN) {
    const navigation = filterNavigationGroupsByVisibility(
      buildNavigationGroupsSuperAdmin(t),
      navigationVisibilityContext,
    );

    return {
      defaultNavigation: navigation,
      settingsNavigation: navigation,
      allNavigation: navigation,
    };
  }

  if (layoutRole === PlatformRole.GUESTERIA_ADMIN) {
    const navigation = filterNavigationGroupsByVisibility(
      buildNavigationGroupsSuperAdmin(t),
      navigationVisibilityContext,
    );

    return {
      defaultNavigation: navigation,
      settingsNavigation: navigation,
      allNavigation: navigation,
    };
  }

  if (layoutRole === "TENANT_ADMIN") {
    const navigation = filterNavigationGroupsByVisibility(
      buildNavigationGroupsAdmin(t),
      navigationVisibilityContext,
    );

    return {
      defaultNavigation: navigation,
      settingsNavigation: navigation,
      allNavigation: navigation,
    };
  }

  const defaultNavigation = filterNavigationGroupsByVisibility(
    buildNavigationGroups(
      t,
      arrivals,
      departures,
      inHouse,
      requestsCount,
      requestsUnreadCount,
      externalTotal,
      externalUnread,
      internalTotal,
      internalUnread,
    ),
    navigationVisibilityContext,
  );

  const settingsNavigation = filterNavigationGroupsByVisibility(
    buildHotelSettingsNavigationGroups(t, getHotelSidebarRole(role)),
    navigationVisibilityContext,
  );
  const allNavigation = [...defaultNavigation, ...settingsNavigation];

  return {
    defaultNavigation,
    settingsNavigation,
    allNavigation,
  };
};
